import { useState, useEffect } from "react";
import Dialog from "./Dialog";
import { useToast } from "../providers/ToastProvider";
import api from "../../services/api";

interface AssistanceResolutionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  item: {
    id: string;
    taskId: string;
    studentId: string;
    studentName: string;
    taskTitle: string;
  } | null;
  onSuccess: () => void;
}

interface SubmissionData {
  contentUrl?: string;
  contentBody?: string;
  evidenceUrl?: string;
  type?: string;
}

export default function AssistanceResolutionDialog({ isOpen, onClose, item, onSuccess }: AssistanceResolutionDialogProps) {
  const { showToast } = useToast();
  const [submission, setSubmission] = useState<SubmissionData | null>(null);
  const [feedback, setFeedback] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const hasSubmission = submission && (
    submission.evidenceUrl || 
    submission.contentUrl || 
    submission.contentBody
  );

  useEffect(() => {
    if (isOpen && item) {
      fetchSubmission();
    }
  }, [isOpen, item]);

  const fetchSubmission = async () => {
    if (!item) return;
    setIsLoading(true);
    try {
      const res = await api.get(`/submissions/single/${item.taskId}/${item.studentId}`);
      setSubmission(res.data);
    } catch (err) {
      console.error("Failed to fetch submission:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResolve = async (submissionStatus: "APPROVED" | "REJECTED") => {
    if (!item) return;
    setIsSubmitting(true);
    try {
      await api.patch(`/service-pool/${item.id}`, {
        status: submissionStatus === "REJECTED" ? "ASSIGNED" : "RESOLVED",
        type: "ASSISTANCE",
        submissionStatus,
        feedback,
        contentUrl: submission?.contentUrl || ""
      });

      showToast(
        submissionStatus === "APPROVED" ? "Mastery Approved" : "Revision Requested",
        `Module status updated for ${item.studentName}`,
        submissionStatus === "APPROVED" ? "success" : "warning"
      );

      onSuccess();
      onClose();
      setFeedback("");
    } catch (err) {
      console.error(err);
      showToast("Error", "Failed to update resolution status", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog 
      isOpen={isOpen} 
      onClose={onClose} 
      title="Strategy Verification"
      size="lg"
    >
      <div className="space-y-6 sm:space-y-8 py-2">
        {/* Header Info */}
        <div className="flex items-center gap-4 bg-surface-container-low p-4 sm:p-5 rounded-[1.5rem] sm:rounded-[2rem] border border-outline-variant/10">
          <div className="w-12 h-12 rounded-2xl bg-primary text-on-primary flex items-center justify-center font-black text-xl shadow-lg flex-shrink-0">
            {item?.studentName?.[0]}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] font-black text-on-surface-variant uppercase tracking-widest opacity-40">Reviewing Implementation for</p>
            <h4 className="text-base sm:text-lg font-black text-on-surface tracking-tight truncate">{item?.studentName}</h4>
            <p className="text-xs font-bold text-primary truncate">{item?.taskTitle}</p>
          </div>
        </div>

        {/* Student Content Area */}
        <div className="space-y-4">
          <h5 className="text-[10px] font-black text-on-surface-variant uppercase tracking-[0.2em] px-1 italic">Student Submission Proof</h5>
          
          {isLoading ? (
            <div className="h-48 bg-surface-container-highest animate-pulse rounded-[1.5rem] sm:rounded-[2.5rem] flex items-center justify-center border border-outline-variant/10">
              <span className="text-[10px] font-bold text-on-surface-variant uppercase tracking-widest animate-pulse">Hydrating Evidence...</span>
            </div>
          ) : submission ? (
            <div className="space-y-4">
              {submission.evidenceUrl && (
                <div className="group relative rounded-[1.5rem] sm:rounded-[2.5rem] overflow-hidden border border-outline-variant/10 shadow-sm bg-black">
                  <img 
                    src={submission.evidenceUrl} 
                    alt="Submission Proof" 
                    className="w-full h-auto max-h-[300px] sm:max-h-[400px] object-contain transition-transform group-hover:scale-[1.02]"
                    onError={(e) => {
                      (e.target as any).src = "https://images.unsplash.com/photo-1590402444681-cd185427744a?q=80&w=2670&auto=format&fit=crop";
                    }}
                  />
                  <a 
                    href={submission.evidenceUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2 text-white font-black text-[10px] uppercase tracking-widest cursor-pointer animate-in fade-in"
                  >
                    <span className="material-symbols-outlined text-sm">open_in_new</span>
                    View Full Evidence
                  </a>
                </div>
              )}

              {submission.contentUrl && (
                <a 
                  href={submission.contentUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-between p-4 sm:p-5 bg-primary/5 border border-primary/20 rounded-2xl group hover:bg-primary/10 transition-all"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="material-symbols-outlined text-primary">link</span>
                    <div className="min-w-0">
                      <p className="text-[10px] font-black uppercase tracking-widest text-primary/60 mb-0.5">Target Deliverable</p>
                      <p className="text-sm font-bold text-on-surface truncate max-w-[200px] sm:max-w-[400px]">{submission.contentUrl}</p>
                    </div>
                  </div>
                  <span className="material-symbols-outlined text-primary group-hover:translate-x-1 transition-transform flex-shrink-0">arrow_forward</span>
                </a>
              )}

              {submission.contentBody && (
                <div className="p-5 sm:p-6 bg-surface-container-highest/30 border border-outline-variant/10 rounded-[1.5rem] sm:rounded-[2rem]">
                  <p className="text-sm font-medium text-on-surface leading-relaxed">{submission.contentBody}</p>
                </div>
              )}
              {!submission.contentUrl && !submission.contentBody && (
                <div className="py-12 text-center bg-surface-container-high/20 rounded-[1.5rem] sm:rounded-[2rem] border border-dashed border-outline-variant/10">
                  <p className="text-[10px] font-bold text-on-surface-variant/40 uppercase tracking-widest">No detailed evidence provided</p>
                </div>
              )}
            </div>
          ) : (
             <p className="text-center py-12 text-on-surface-variant/40">Failed to load submission data</p>
          )}
        </div>

        {/* Coach Action Area */}
        <div className="space-y-4 sm:space-y-5 pt-4 border-t border-outline-variant/10">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-on-surface-variant uppercase tracking-widest px-1 flex justify-between">
              <span>Verification Note / Revision Feedback</span>
              <span className="text-red-500 lowercase font-bold italic opacity-60">Required for Rejection</span>
            </label>
            <textarea 
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Provide constructive feedback (required for rejection)..."
              className="w-full p-4 sm:p-6 bg-surface-container-high text-on-surface border border-outline-variant/10 rounded-xl sm:rounded-[1.5rem] text-sm font-medium focus:ring-2 focus:ring-primary/20 outline-none transition-all resize-none"
              rows={4}
            />
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4">
            <button 
              onClick={() => handleResolve("REJECTED")}
              disabled={isSubmitting || !feedback || !hasSubmission}
              className={`py-4 sm:py-5 rounded-xl sm:rounded-[1.75rem] font-black text-[11px] uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 ${
                (!feedback || !hasSubmission) ? "bg-surface-container-high text-on-surface-variant/20 cursor-not-allowed border border-outline-variant/10" : "bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white shadow-xl shadow-red-500/10 border border-red-500/10"
              }`}
            >
              <span className="material-symbols-outlined text-sm">assignment_return</span>
              Reject
            </button>
            <button 
              onClick={() => handleResolve("APPROVED")}
              disabled={isSubmitting || !hasSubmission}
              className={`py-4 sm:py-5 rounded-xl sm:rounded-[1.75rem] font-black text-[11px] uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-2 ${
                !hasSubmission ? "bg-surface-container-high text-on-surface-variant/20 cursor-not-allowed border border-outline-variant/10" : "bg-emerald-500 text-white shadow-xl shadow-emerald-500/20 hover:translate-y-[-4px] active:scale-[0.98]"
              }`}
            >
              <span className="material-symbols-outlined text-sm">verified</span>
              Verify Mastery
            </button>
          </div>
          {!hasSubmission && !isLoading && (
            <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest text-center animate-pulse">
              Resolution Locked: Awaiting Student Evidence
            </p>
          )}
        </div>
      </div>
    </Dialog>
  );
}
